import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type React from "react";
import { cn } from "../../utils/cn";
import { Text } from "../text/text";

type BadgeVariant = "default" | "secondary" | "outline" | "destructive";

interface BadgeProps {
  text: string;
  variant: BadgeVariant;
  size?: "default" | "sm";
  fontWeight?: "default" | "bold";
  icon?: React.ReactNode;
  disabled?: boolean;
  asChild?: boolean;
  className?: string;
  onClick?: () => void;
}

const badgeClasses = cva(
  "inline-flex items-center gap-1 outline-none transition-[color,box-shadow] focus:ring-[2px] focus:ring-black focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "bg-primary shadow-sm hover:bg-primary/80 [a&]:hover:bg-primary/80",
        secondary:
          "bg-secondary hover:bg-secondary/80 [a&]:hover:bg-secondary/80",
        outline:
          "border border-zinc-700 bg-transparent hover:bg-muted [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        destructive:
          "bg-destructive hover:bg-red-700 [a&]:hover:bg-destructive/80",
      },
      size: {
        default: "h-5 rounded-md px-2.5 py-0.5",
        sm: "h-4 rounded-md px-1 py-0.5",
      },
      disabled: {
        true: "pointer-events-none cursor-not-allowed opacity-80",
        false: "cursor-pointer",
      },
    },
    compoundVariants: [
      {
        variant: "default",
        disabled: true,
        className: "bg-muted-foreground",
      },
      {
        variant: "secondary",
        disabled: true,
        className:
          "bg-secondary text-muted-foreground [&_*]:text-muted-foreground",
      },
      {
        variant: "outline",
        disabled: true,
        className:
          "border-border text-muted-foreground [&_*]:text-muted-foreground",
      },
      {
        variant: "destructive",
        disabled: true,
        className: "bg-muted-foreground",
      },
    ],
    defaultVariants: {
      size: "default",
      disabled: false,
    },
  }
);

export function Badge(props: Readonly<BadgeProps>): React.ReactNode {
  const size = props.size ?? "default";
  const fontWeight = props.fontWeight ?? "default";
  const disabled = props.disabled ?? false;
  const asChild = props.asChild ?? false;
  const Comp = asChild ? Slot : "span";

  const iconSizeClasses = size === "sm" ? "h-3 w-3" : "h-4 w-4";

  const textColor =
    props.variant === "secondary" || props.variant === "outline"
      ? "black"
      : "white";

  const textVariant = fontWeight === "bold" ? "body-sm-bold" : "body-sm";

  return (
    <Comp
      aria-disabled={disabled || undefined}
      className={cn(
        badgeClasses({ variant: props.variant, size, disabled }),
        props.className
      )}
      tabIndex={disabled ? -1 : 0}
    >
      {props.icon ? (
        <span
          aria-hidden="true"
          className={`shrink-0 ${iconSizeClasses}`}
          onClick={props.onClick}
        >
          {props.icon}
        </span>
      ) : null}
      <Text color={textColor} variant={textVariant}>
        {props.text}
      </Text>
    </Comp>
  );
}
